import { createContext, use, type ReactNode } from 'react';
import type { CellContext } from '@tanstack/react-table';
import type { DataTableFeatures } from '@customerio/pluma-components/react';
import type { Tag } from '@customerio/hydra-ui-api';
import type { AttributeScope } from '@/services/db/data-index/entity';
import type { IdentifiersConfig } from '@/features/people';
import { CIO_GEO_ATTRIBUTES, CREATED_IN_CIO_AT } from '@/utils/attributes';
import type { DataIndexItemKind } from '../utils/types';
import type { DataIndexTableRow } from '../utils/table-row';
import { isEditableByWorkspace } from '../utils/editing';
import { legacyDataIndexItemPath } from '../urls';
import { DescriptionEditor } from './description-editor';
import { NameCell } from './name-cell';
import { TagEditor } from './tag-editor';

/**
 * The two cells that hold what somebody is typing.
 *
 * They are components of their own, at module scope, because the table
 * renders a column's `cell` through `flexRender`, which turns it into an
 * element type: a `cell` written inline is a new function on every rebuild of
 * the column definitions, and a new type is a remount rather than a
 * re-render. The list rebuilds them whenever a count, a usage read or a tag
 * lands — all of which arrive after the rows are on screen — so an inline
 * cell threw away whatever was half-typed in it, without saying so.
 *
 * What they need comes through a context instead of through the definitions,
 * so the values can change while the cell stays the same component.
 */

/** The bulk edit these cells take part in, while one is happening. */
export type EditableCellsBulk = {
  /** Which edit this is. A write from an earlier one is discarded. */
  session: number;
  descriptionOf: (name: string) => string;
  tagIdsOf: (name: string) => number[];
  setDescription: (name: string, description: string, forSession: number) => void;
  setTagIds: (name: string, next: (chosen: number[]) => number[], forSession: number) => void;
  /** Told while a cell is naming a tag, so the bar's Save can wait for it. */
  onNaming: (isNaming: boolean) => void;
};

type EditableCellsValue = {
  workspaceId: number;
  kind: DataIndexItemKind;
  scope: AttributeScope;
  identifiers: IdentifiersConfig | null | undefined;
  /** Every tag the rows on this page were written with, to name them by id. */
  tags: readonly Tag[];
  /** Whether the viewer may edit attribute metadata at all. */
  canEdit: boolean;
  bulk: EditableCellsBulk | null;
  onSaveDescription: (name: string, description: string) => Promise<boolean>;
  onSaveTags: (name: string, tagIds: number[]) => Promise<boolean>;
};

/** The attributes Customer.io writes for every workspace, which it also names. */
const AUTOGENERATED_ATTRIBUTES: readonly string[] = ['cio_id', CREATED_IN_CIO_AT, ...CIO_GEO_ATTRIBUTES];

const EditableCellsContext = createContext<EditableCellsValue | null>(null);

function useEditableCells(): EditableCellsValue {
  const value = use(EditableCellsContext);
  if (value == null) {
    throw new Error('A data-index editable cell was rendered outside its table.');
  }
  return value;
}

type EditableCellsProviderProps = {
  value: EditableCellsValue;
  children: ReactNode;
};

export function EditableCellsProvider({ value, children }: EditableCellsProviderProps) {
  return <EditableCellsContext value={value}>{children}</EditableCellsContext>;
}

type CellProps = CellContext<DataTableFeatures, DataIndexTableRow>;

/** The row's name, what is known about it, and its description in place. */
export function NameEditorCell({ row }: CellProps) {
  const { workspaceId, kind, scope, identifiers, canEdit, bulk, onSaveDescription } = useEditableCells();
  const name = row.original.id;

  return (
    <NameCell
      name={name}
      href={legacyDataIndexItemPath(String(workspaceId), kind, name, scope)}
      isSensitive={row.original.isSensitive}
      isAutogenerated={kind === 'attribute' && AUTOGENERATED_ATTRIBUTES.includes(name)}
      identifiers={identifiers}
      withAttributeBadges={kind === 'attribute'}
      description={
        <DescriptionEditor
          workspaceId={workspaceId}
          name={name}
          type={kind === 'event' ? 'event' : 'attribute'}
          objectTypeId={scope.kind === 'profile' ? undefined : scope.objectTypeId}
          description={row.original.description}
          canEdit={canEdit && isEditableByWorkspace(kind, name)}
          hasPermission={canEdit}
          onSave={(description) => onSaveDescription(name, description)}
          bulk={
            bulk == null
              ? undefined
              : {
                  value: bulk.descriptionOf(name),
                  onChange: (value) => bulk.setDescription(name, value, bulk.session),
                }
          }
        />
      }
    />
  );
}

/** The row's tags, changed in place. */
export function TagsEditorCell({ row }: CellProps) {
  const { workspaceId, kind, tags, canEdit, bulk, onSaveTags } = useEditableCells();
  const name = row.original.id;
  const tagsById = new Map(tags.map((tag) => [tag.id, tag]));

  return (
    <TagEditor
      workspaceId={workspaceId}
      tagIds={row.original.tagIds}
      tags={row.original.tagIds.flatMap((id) => tagsById.get(id) ?? [])}
      canEdit={canEdit && isEditableByWorkspace(kind, name)}
      hasPermission={canEdit}
      onSave={(tagIds) => onSaveTags(name, tagIds)}
      bulk={
        bulk == null
          ? undefined
          : {
              tagIds: bulk.tagIdsOf(name),
              onChange: (next) => bulk.setTagIds(name, next, bulk.session),
              onNaming: bulk.onNaming,
            }
      }
    />
  );
}
